import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import type { Incident } from '@/signalr';
import { elsRecordQueryKey, fetchLatestElsData } from './api/elsApi';
import {
  fetchEedAdditionalData,
  requestEedMedia as requestEedMediaApi,
  type EedCallCapabilities,
  type EedCallInformation,
  type EedCallerInformation,
} from './api/incidentDetailApi';
import { DebugInfoPanel } from './subpanels/DebugInfoPanel';
import { ElsDataPanel } from './subpanels/ElsDataPanel';
import { IncidentBaseInfoPanel } from './subpanels/IncidentBaseInfoPanel';
import { CallerLocationPanel } from './subpanels/caller-location/CallerLocationPanel';
import { EedCallerInfoPanel } from './subpanels/caller-info/EedCallerInfoPanel';
import { MobileAppCallerInfoPanel } from './subpanels/caller-info/MobileAppCallerInfoPanel';
import { WebAppCallerInfoPanel } from './subpanels/caller-info/WebAppCallerInfoPanel';
import { AdditionalIncidentDataPanel } from './subpanels/additional-data/AdditionalIncidentDataPanel';
import { UploadedMediaPanel } from './subpanels/uploaded-media/UploadedMediaPanel';

export type IncidentDataPanelProps = {
  incident: Incident;
  incidentIsReadOnly: boolean;
  eedCallCapabilities: readonly EedCallCapabilities[];
};

export function IncidentDataPanel({ incident, incidentIsReadOnly, eedCallCapabilities }: IncidentDataPanelProps) {
  const psapConfiguration = usePsapConfiguration();

  const [eedCallInformation, setEedCallInformation] = useState<EedCallInformation | null>(null);
  const [eedMedicalData, setEedMedicalData] = useState<EedCallerInformation | null>(null);
  const [eedBatteryLevel, setEedBatteryLevel] = useState<string | null>(null);
  const [askedForEedMedicalData, setAskedForEedMedicalData] = useState(false);
  const [eedMediaRequested, setEedMediaRequested] = useState(false);

  const showEedPanel = psapConfiguration.appleEedAllowMedicalData && !incidentIsReadOnly;

  const callerPhoneNumbers = incident.caller.phoneNumbers;
  const { data: elsRecord } = useQuery({
    queryKey: elsRecordQueryKey(incident.id, callerPhoneNumbers),
    queryFn: () => fetchLatestElsData(callerPhoneNumbers),
    enabled: callerPhoneNumbers.length > 0,
  });

  const getEedMedicalData = async () => {
    const validPhoneNumber = eedCallCapabilities.find((capability) => capability.hasAdditionalData)?.phoneNumber;
    if (validPhoneNumber == null) {
      return;
    }

    const additionalData = await fetchEedAdditionalData(validPhoneNumber);
    setAskedForEedMedicalData(true);
    if (additionalData === null) {
      return;
    }
    setEedCallInformation(additionalData.additionalData?.call_information ?? null);
    setEedMedicalData(additionalData.additionalData?.caller_information ?? null);
    setEedBatteryLevel(additionalData.batteryLevel ?? null);
  };

  const requestEedMedia = async () => {
    const validPhoneNumber = eedCallCapabilities.find((capability) => capability.hasMediaUploadCapability)?.phoneNumber;
    if (validPhoneNumber == null) {
      return;
    }

    await requestEedMediaApi(incident.id, validPhoneNumber);
    setEedMediaRequested(true);
  };

  const lastPosition = incident.lastPosition;

  return (
    <div className="d-flex flex-column gap-3">
      <DebugInfoPanel incidentId={incident.id} appInfo={incident.caller.appInfo} deviceInfo={incident.caller.deviceInfo} />

      <IncidentBaseInfoPanel
        isCallFromMobileApp={incident.isCallFromMobileApp}
        appDisplayName={incident.appDisplayName}
        closedTimeUtc={incident.closedTimeUtc}
        createdTimeUtc={incident.createdTimeUtc}
        incidentInvolvedPsapStates={incident.involvedPsaps}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      <CallerLocationPanel
        incidentId={incident.id}
        incidentIsReadOnly={incidentIsReadOnly}
        callerPosition={lastPosition}
        callerPositionHistory={incident.positions}
        caller={incident.caller}
        chatIsActive={incident.chatId != null}
        deviceInfo={incident.caller.deviceInfo}
        eedBatteryLevel={eedBatteryLevel}
      />

      {showEedPanel && (
        <EedCallerInfoPanel
          incidentId={incident.id}
          eedCallInformation={eedCallInformation}
          eedMedicalData={eedMedicalData}
          eedCallCapabilities={eedCallCapabilities}
          askedForEedMedicalData={askedForEedMedicalData}
          askedForEedMedia={eedMediaRequested}
          getEedMedicalData={getEedMedicalData}
          requestEedMedia={requestEedMedia}
        />
      )}

      {incident.attachments.length > 0 && <UploadedMediaPanel media={incident.attachments} />}

      {elsRecord != null && <ElsDataPanel elsRecord={elsRecord} />}

      {incident.isCallFromMobileApp ? (
        <MobileAppCallerInfoPanel
          incidentId={incident.id}
          incidentIsReadOnly={incidentIsReadOnly}
          createdTimeUtc={incident.createdTimeUtc}
          caller={incident.caller}
          patient={incident.patient}
          classification={incident.caller.classification}
        />
      ) : (
        <WebAppCallerInfoPanel incidentId={incident.id} incidentIsReadOnly={incidentIsReadOnly} caller={incident.caller} />
      )}

      <AdditionalIncidentDataPanel
        incidentId={incident.id}
        additionalIncidentData={incident.additionalIncidentData}
        incidentIsReadOnly={incidentIsReadOnly}
        dispatcher={incident.dispatcher}
      />
    </div>
  );
}
